import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject, signal, computed } from '@angular/core';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { AuthUiService } from '../../../services/auth-ui.service';
import { ConfigurationService } from '../../../services/configuration.service';
import { BehaviorSubject, Subscription, take, map } from 'rxjs';
import { GenericError, ErrorType } from '../../../models/error.model';
import { LoginSession } from '../../../models/login-session.model';
import { ErrorService } from '../../../services/error.service';
import { CONSUMER_PORTAL_ADMIN_APP_CONFIG } from '../../../customer-portal-admin-ui.module';
import { ConsumerPortalAdminConfig } from '../../../models/consumer-portal-admin-config';
import { TranslateService } from '@ngx-translate/core';
import { AppEventService } from '../../../services/app-event.service';
import { ComponentsToLoad } from '../../../enums/components-to-load.enum';
import { SiteSettingService } from '../../../services/site-setting.service';
import { AppFacadeService } from '../../../services/app-facade.service';
import { AppMediatorService } from '../../../services/app-mediator.service';

@Component({
    standalone: false,
    selector: 'por-consumer-portal-admin',
    templateUrl: './admin.component.html',
    styleUrls: ['./admin.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerPortalAdminComponent implements OnInit, OnChanges, OnDestroy {
    private readonly adminAppConfig = inject<ConsumerPortalAdminConfig>(CONSUMER_PORTAL_ADMIN_APP_CONFIG);
    private readonly authService = inject(AuthUiService);
    readonly configService = inject(ConfigurationService);
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly errorService = inject(ErrorService);
    private readonly translateService = inject(TranslateService);
    private readonly appEventService = inject(AppEventService);
    private readonly siteSetting = inject(SiteSettingService);
    private readonly appMediatorService = inject(AppMediatorService);
    readonly appFacadeService = inject(AppFacadeService);

    subscriptions: Subscription[] = [];
    isMobile = false;
    errorMessage$ = this.errorService.error$;
    orgConfig$ = this.configService.orgConfig$.pipe(
        map(config => {
            const inputs: ConsumerPortalAdminConfig = {
                ...(JSON.parse(this.config) as ConsumerPortalAdminConfig),
                styling: {
                    themeColors: {
                        primaryColor: config?.PrimaryColor,
                        secondaryColor: config?.SecondaryColor
                    }
                }
            };
            this.siteSetting.setStylingForAdminSite(inputs);
            return config;
        })
    );
    appliedClass = '';
    @Input() config!: string;
    @Output() readonly errorOutput: EventEmitter<GenericError> = new EventEmitter<GenericError>();
    doScrollToTop = false;

    parsedOrgId = '';
    parsedEmail = '';

    // Onboarding context passed to <por-cp-onboard-panel>; the panel owns the flow state.
    isProduction = signal(false);
    onboardConfigCredentials = signal<{ cognitoToken?: string; relationshipId?: string }>({});

    currentErrorType = signal<string | null>(null);
    orgConfigFailed = false;

    isOnboardingEligible = computed(() => {
        // Any blocking CP/CL error (not-onboarded 404 OR revoked-key 401) should let a
        // POR user recover via onboarding, since GAdmin can't fix CP/CL-side problems.
        const errorType = this.currentErrorType();
        const isBlockingError = errorType === ErrorType.InvalidSubDomainOrgId || errorType === ErrorType.AuthFailed;
        return isBlockingError && this.parsedEmail.endsWith('@pointofrental.com');
    });

    versionLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    constructor() {
        const adminAppConfig = this.adminAppConfig;

        this.configService.setAdminConfigurations(undefined, adminAppConfig);

        // Initialize app event service
        this.appEventService.initialize();
        this.authService.initAuth();
    }

    ngOnInit(): void {
        // Subscribe to errors FIRST — so orgConfigFailed flag is set before adminInputs$ emits
        this.subscriptions.push(
            this.errorService.error$.subscribe(result => {
                if (result) {
                    this.emitError(result);
                    this.currentErrorType.set(result.errorType);
                    if (result.errorType === ErrorType.InvalidSubDomainOrgId || result.errorType === ErrorType.AuthFailed) {
                        this.orgConfigFailed = true;
                    }
                }
            })
        );

        this.subscriptions.push(
            this.configService.adminInputs$.pipe(take(1)).subscribe(inputs => {
                const logginSession: LoginSession = {
                    ...(inputs.Auth as LoginSession),
                    initialLogin: false,
                    /* eslint-disable @typescript-eslint/naming-convention */
                    /**
                     * Note camelCase:DB Model
                     */
                    OrganizationType: inputs.organizationType,
                    OrganizationId: inputs.organizationId,
                    isAdmin: true
                };

                // Skip auth refresh and version check when the org's CP/CL is in a
                // blocking error state (not-onboarded 404 or revoked-key 401). These
                // calls would always fail and only add noise; the onboarding flow takes over.
                if (!this.orgConfigFailed) {
                    this.handleRefreshToken(logginSession);
                    if (!this.isSyrnix()) {
                        this.getOrganizationLicense(logginSession?.OrganizationId);
                    }
                } else {
                    // Mark admin as loaded so the loading overlay stays visible with the error message
                    this.appFacadeService.setAdminLoaded(true);
                }
            })
        );

        this.subscriptions.push(
            this.breakpointObserver.observe(['(max-width: 992px)']).subscribe((result: BreakpointState) => {
                this.isMobile = result.matches;
            })
        );

        const lang = this.appMediatorService.localStorageService.selectedContentLanguage;
        if (lang && lang !== this.translateService.getCurrentLang()) {
            this.translateService.use(lang);
        }
        this.appFacadeService.setActiveTab(ComponentsToLoad.AdminOverview);
        this.parseConfigForOnboarding();
    }

    ngOnChanges(changes: SimpleChanges): void {
        Object.entries(changes).forEach(([key, value]) => {
            if (key === 'config' && value !== undefined) {
                this.configService.setAdminConfigurations(value.currentValue, this.adminAppConfig);
                this.config = value.currentValue;
                this.parseConfigForOnboarding();
                return;
            }
        });
    }

    /**
     * This will subscribe and autorefresh token every 5 minutes
     * Refresh token is stored in localStorage so it can be used anytime to check if user has one active
     */
    handleRefreshToken(authDataObj: LoginSession): void {
        this.subscriptions.push(
            this.authService.activateRefreshAuthTimer(authDataObj).subscribe({
                next: () => {
                    this.appFacadeService.setAdminLoaded(false);
                },
                error: () => {
                    this.appFacadeService.setAdminLoaded(true);
                }
            })
        );
    }

    // Emite error to the parent component using the errorOutput event
    private emitError(error: GenericError): void {
        this.errorOutput.emit(error);
    }

    private parseConfigForOnboarding(): void {
        try {
            const parsed = JSON.parse(this.config) as ConsumerPortalAdminConfig;
            this.parsedOrgId = parsed.organizationId || '';
            this.parsedEmail = parsed.Auth?.Email || '';
            this.isProduction.set(parsed.production ?? false);
            this.onboardConfigCredentials.set({ cognitoToken: parsed.cognitoToken, relationshipId: parsed.relationshipId });
        } catch {
            // Config not parseable yet
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.map(sub => sub.unsubscribe());
        this.appEventService.destroy();
    }

    getOrganizationLicense(organizationId: string | undefined): void {
        if (organizationId !== undefined) {
            this.subscriptions.push(
                this.appFacadeService.versionToggleService.getOrgVersion(organizationId).subscribe({
                    next: () => {
                        this.versionLoaded$.next(true);
                    },
                    error: () => {
                        // Still mark as loaded on error — child components default to Basic version safely.
                        // Non-onboarded orgs have no IAM credentials so this call fails; the panel
                        // should still render so the onboarding section can be shown.
                        this.versionLoaded$.next(true);
                    }
                })
            );
        }
    }

    isSyrnix(): boolean {
        return this.appFacadeService.featureToggleService.isSyrinx() ? true : false;
    }

    resetScrollFlag(): void {
        this.doScrollToTop = false;
        this.appliedClass = '';
    }

    onClassReceived(cssClass: string) {
        this.appliedClass = cssClass;
    }
}
